import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";

const MIN_THUMB_HEIGHT = 24;
const TRACK_WIDTH = 6;
const SCROLLBAR_WIDTH = 14;
const ARROW_STEP = 40;
const ARROW_REPEAT_DELAY = 350;
const ARROW_REPEAT_INTERVAL = 80;

const styles = theme => ({
  root: {
    position: "relative",
    marginBottom: "26px",
    textAlign: "left"
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "6px"
  },
  title: {
    margin: 0,
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: 1.5,
    color: "#3D4051"
  },
  count: {
    minWidth: "20px",
    padding: "1px 6px",
    backgroundColor: "#F1F1F1",
    borderRadius: "2px",
    fontSize: "11px",
    fontWeight: 500,
    lineHeight: "18px",
    textAlign: "center",
    color: "#3D4051"
  },
  box: {
    position: "relative",
    display: "flex",
    alignItems: "stretch",
    overflow: "hidden",
    border: "1px solid #E1E1E1",
    borderRadius: "2px",
    backgroundColor: "#fff",
    transition: "border-color 0.18s"
  },
  box_error: {
    borderColor: "#D93025"
  },
  list: {
    flex: 1,
    minWidth: 0,
    maxHeight: "142px",
    overflowY: "auto",
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      display: "none"
    }
  },
  item: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    height: "40px",
    margin: 0,
    padding: "0 12px 0 13px",
    borderTop: "1px solid #F1F1F1",
    fontSize: "13.5px",
    fontWeight: "normal",
    color: "#1C1C1C",
    cursor: "pointer",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
    transition: "background-color 0.15s",
    "&:first-child": {
      borderTop: "none"
    },
    "&:hover": {
      backgroundColor: "#FCFCFA"
    },
    "&:hover $radio": {
      borderColor: "#A9A9A9"
    }
  },
  item_selected: {
    backgroundColor: "#FFF8E1",
    "&:hover": {
      backgroundColor: "#FFF8E1"
    },
    "&:hover $radio": {
      borderColor: "#FFC107"
    },
    "& $email": {
      fontWeight: 500
    }
  },
  input: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "1px",
    height: "1px",
    margin: 0,
    opacity: 0,
    pointerEvents: "none",
    "&:focus-visible + $radio": {
      boxShadow: "0 0 0 3px rgba(255, 193, 7, 0.35)"
    }
  },
  radio: {
    flex: "0 0 18px",
    boxSizing: "border-box",
    width: "18px",
    height: "18px",
    marginRight: "11px",
    border: "1.5px solid #C8C8C8",
    borderRadius: "50%",
    backgroundColor: "#fff",
    transition: "border-color 0.15s, border-width 0.15s"
  },
  radio_selected: {
    borderWidth: "5px",
    borderColor: "#FFC107"
  },
  email: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  scrollbar: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: `0 0 ${SCROLLBAR_WIDTH}px`,
    width: `${SCROLLBAR_WIDTH}px`,
    padding: "3px 0"
  },
  arrow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 10px",
    width: "100%",
    height: "10px",
    padding: 0,
    border: "none",
    background: "none",
    cursor: "pointer",
    touchAction: "none",
    "&:before": {
      content: '""',
      width: 0,
      height: 0,
      borderLeft: "4px solid transparent",
      borderRight: "4px solid transparent",
      transition: "border-color 0.15s, opacity 0.15s"
    },
    "&:focus": {
      outline: "none"
    }
  },
  arrow_up: {
    "&:before": {
      borderBottom: "5px solid #FFC107"
    },
    "&:hover:before": {
      borderBottomColor: "#E9AC00"
    }
  },
  arrow_down: {
    "&:before": {
      borderTop: "5px solid #FFC107"
    },
    "&:hover:before": {
      borderTopColor: "#E9AC00"
    }
  },
  arrow_disabled: {
    cursor: "default",
    "&:before": {
      opacity: 0.35
    }
  },
  track: {
    position: "relative",
    flex: 1,
    width: `${TRACK_WIDTH}px`,
    margin: "3px 0",
    backgroundColor: "#F4F4F2",
    borderRadius: `${TRACK_WIDTH / 2}px`,
    cursor: "pointer",
    touchAction: "none"
  },
  thumb: {
    position: "absolute",
    left: 0,
    width: `${TRACK_WIDTH}px`,
    backgroundColor: "#FFC107",
    borderRadius: `${TRACK_WIDTH / 2}px`,
    transition: "background-color 0.15s",
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      bottom: 0,
      left: "-8px",
      right: "-2px"
    },
    "&:hover": {
      backgroundColor: "#E9AC00"
    }
  },
  thumb_dragging: {
    backgroundColor: "#E9AC00"
  },
  fade: {
    position: "absolute",
    left: 0,
    right: `${SCROLLBAR_WIDTH}px`,
    bottom: 0,
    height: "22px",
    background: "linear-gradient(rgba(255, 255, 255, 0), #fff)",
    pointerEvents: "none",
    transition: "opacity 0.2s"
  },
  fade_hidden: {
    opacity: 0
  },
  error: {
    position: "absolute",
    left: 0,
    right: 0,
    top: "calc(100% + 3px)",
    fontSize: "12px",
    lineHeight: "16px",
    color: "#D93025",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    animation: "$errorIn 0.18s ease"
  },
  "@keyframes errorIn": {
    from: { opacity: 0, transform: "translateY(-3px)" },
    to: { opacity: 1, transform: "none" }
  }
});

const sameEmail = (a, b) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

class CashboxApprovalEmails extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      scrollable: false,
      thumbHeight: 0,
      thumbTop: 0,
      atStart: true,
      atEnd: true,
      dragging: false
    };
    this.listRef = React.createRef();
    this.trackRef = React.createRef();
    this.drag = null;
  }

  componentDidMount() {
    this.measure();
    window.addEventListener("resize", this.measure);
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.emails !== this.props.emails ||
      (!prevState.scrollable && this.state.scrollable)
    ) {
      this.measure();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.measure);
    this.stopArrowRepeat();
  }

  trackHeight = () => {
    const track = this.trackRef.current;
    const list = this.listRef.current;

    return track ? track.clientHeight : list ? list.clientHeight : 0;
  };

  measure = () => {
    const list = this.listRef.current;

    if (!list) {
      return;
    }

    const { clientHeight, scrollHeight, scrollTop } = list;
    const maxScroll = scrollHeight - clientHeight;

    if (maxScroll <= 1) {
      if (this.state.scrollable) {
        this.setState({
          scrollable: false,
          thumbHeight: 0,
          thumbTop: 0,
          atStart: true,
          atEnd: true
        });
      }
      return;
    }

    const trackHeight = this.trackHeight();
    const thumbHeight = Math.min(
      trackHeight,
      Math.max(MIN_THUMB_HEIGHT, (trackHeight * clientHeight) / scrollHeight)
    );
    const thumbTop = (scrollTop / maxScroll) * (trackHeight - thumbHeight);

    this.setState({
      scrollable: true,
      thumbHeight,
      thumbTop,
      atStart: scrollTop <= 1,
      atEnd: scrollTop >= maxScroll - 1
    });
  };

  scrollStep = (direction, smooth) => {
    const list = this.listRef.current;

    if (list) {
      list.scrollBy({
        top: direction * ARROW_STEP,
        behavior: smooth ? "smooth" : "auto"
      });
    }
  };

  startArrowRepeat = (event, direction) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    this.stopArrowRepeat();
    this.scrollStep(direction, true);
    this.arrowDelay = setTimeout(() => {
      this.arrowInterval = setInterval(
        () => this.scrollStep(direction, false),
        ARROW_REPEAT_INTERVAL
      );
    }, ARROW_REPEAT_DELAY);
  };

  stopArrowRepeat = () => {
    clearTimeout(this.arrowDelay);
    clearInterval(this.arrowInterval);
  };

  handleArrowUpPointerDown = event => this.startArrowRepeat(event, -1);

  handleArrowDownPointerDown = event => this.startArrowRepeat(event, 1);

  handleThumbPointerDown = event => {
    const list = this.listRef.current;

    if (!list) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    this.drag = { startY: event.clientY, startScrollTop: list.scrollTop };
    this.setState({ dragging: true });
  };

  handleTrackPointerDown = event => {
    const list = this.listRef.current;

    if (!list || event.target !== event.currentTarget) {
      return;
    }

    event.preventDefault();

    const { clientHeight, scrollHeight } = list;
    const { thumbHeight } = this.state;
    const travel = this.trackHeight() - thumbHeight;

    if (travel <= 0) {
      return;
    }

    const offsetY =
      event.clientY - event.currentTarget.getBoundingClientRect().top;
    const ratio = Math.min(1, Math.max(0, (offsetY - thumbHeight / 2) / travel));

    list.scrollTo({
      top: ratio * (scrollHeight - clientHeight),
      behavior: "smooth"
    });
  };

  handleThumbPointerMove = event => {
    const list = this.listRef.current;

    if (!this.drag || !list) {
      return;
    }

    const { clientHeight, scrollHeight } = list;
    const travel = this.trackHeight() - this.state.thumbHeight;

    if (travel <= 0) {
      return;
    }

    list.scrollTop =
      this.drag.startScrollTop +
      ((event.clientY - this.drag.startY) * (scrollHeight - clientHeight)) /
        travel;
  };

  handleThumbPointerUp = event => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    this.drag = null;
    this.setState({ dragging: false });
  };

  render() {
    const { classes, emails, selectedEmail, onSelect, errorText } = this.props;
    const {
      scrollable,
      thumbHeight,
      thumbTop,
      atStart,
      atEnd,
      dragging
    } = this.state;

    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <div className={classes.title} id="cashbox-approval-emails-title">
            Approve with one of these emails
          </div>
          <span
            className={classes.count}
            title={`${emails.length} ${emails.length === 1 ? "email" : "emails"}`}
          >
            {emails.length}
          </span>
        </div>
        <div
          className={classes.box + (errorText ? " " + classes.box_error : "")}
        >
          <div
            ref={this.listRef}
            className={classes.list}
            role="radiogroup"
            aria-labelledby="cashbox-approval-emails-title"
            onScroll={this.measure}
          >
            {emails.map(email => {
              const selected = sameEmail(email, selectedEmail);

              return (
                <label
                  key={email}
                  title={email}
                  className={
                    classes.item + (selected ? " " + classes.item_selected : "")
                  }
                >
                  <input
                    type="radio"
                    name="cashbox-approval-email"
                    value={email}
                    checked={selected}
                    className={classes.input}
                    onChange={() => onSelect(email)}
                  />
                  <span
                    className={
                      classes.radio +
                      (selected ? " " + classes.radio_selected : "")
                    }
                  />
                  <span className={classes.email}>{email}</span>
                </label>
              );
            })}
          </div>
          {scrollable && (
            <div className={classes.scrollbar} aria-hidden="true">
              <button
                type="button"
                tabIndex={-1}
                className={
                  classes.arrow +
                  " " +
                  classes.arrow_up +
                  (atStart ? " " + classes.arrow_disabled : "")
                }
                onPointerDown={this.handleArrowUpPointerDown}
                onPointerUp={this.stopArrowRepeat}
                onPointerCancel={this.stopArrowRepeat}
              />
              <div
                ref={this.trackRef}
                className={classes.track}
                onPointerDown={this.handleTrackPointerDown}
              >
                <div
                  className={
                    classes.thumb + (dragging ? " " + classes.thumb_dragging : "")
                  }
                  style={{ height: thumbHeight, top: thumbTop }}
                  onPointerDown={this.handleThumbPointerDown}
                  onPointerMove={this.handleThumbPointerMove}
                  onPointerUp={this.handleThumbPointerUp}
                  onPointerCancel={this.handleThumbPointerUp}
                />
              </div>
              <button
                type="button"
                tabIndex={-1}
                className={
                  classes.arrow +
                  " " +
                  classes.arrow_down +
                  (atEnd ? " " + classes.arrow_disabled : "")
                }
                onPointerDown={this.handleArrowDownPointerDown}
                onPointerUp={this.stopArrowRepeat}
                onPointerCancel={this.stopArrowRepeat}
              />
            </div>
          )}
          {scrollable && (
            <div
              className={classes.fade + (atEnd ? " " + classes.fade_hidden : "")}
            />
          )}
        </div>
        {errorText && (
          <span className={classes.error} role="alert">
            {errorText}
          </span>
        )}
      </div>
    );
  }
}

CashboxApprovalEmails.propTypes = {
  classes: PropTypes.object.isRequired,
  emails: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedEmail: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  errorText: PropTypes.string
};

export default withStyles(styles)(CashboxApprovalEmails);
